import React, { memo } from "react";
import { Link, useLocation } from "react-router-dom";

function normalizePathname(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const clean = raw.replace(/\/+$/, "");
  return clean || "/";
}

function toParts(to) {
  if (!to) return { pathname: "", search: "" };

  if (typeof to === "string") {
    try {
      const url = new URL(to, window.location.origin);
      return {
        pathname: normalizePathname(url.pathname),
        search: url.search || "",
      };
    } catch {
      const [pathnamePart, searchPart = ""] = String(to).split("?");
      return {
        pathname: normalizePathname(pathnamePart),
        search: searchPart ? `?${searchPart}` : "",
      };
    }
  }

  return {
    pathname: normalizePathname(to.pathname || ""),
    search: to.search || "",
  };
}

function queryValue(search, key) {
  try {
    return new URLSearchParams(search || "").get(key) || "";
  } catch {
    return "";
  }
}

function normalizeTab(search) {
  return queryValue(search, "tab") || "overview";
}

function relevantSearchMatches(currentSearch, targetSearch) {
  const targetTab = normalizeTab(targetSearch);
  const currentTab = normalizeTab(currentSearch);

  if (targetTab !== currentTab) return false;

  const ignored = new Set([
    "returnTo",
    "from",
    "sourceTab",
    "offerId",
    "offerView",
  ]);

  try {
    const target = new URLSearchParams(targetSearch || "");
    const current = new URLSearchParams(currentSearch || "");

    for (const [key, value] of target.entries()) {
      if (ignored.has(key) || key === "tab") continue;
      if ((current.get(key) || "") !== value) return false;
    }

    return true;
  } catch {
    return targetTab === currentTab;
  }
}

function isActiveLink(location, item) {
  const target = toParts(item?.to);
  if (!target.pathname) return false;

  const currentPath = normalizePathname(location?.pathname || "");
  const currentSearch = location?.search || "";
  const targetPath = target.pathname;
  const targetSearch = target.search || "";

  const pathMatches = item?.end
    ? currentPath === targetPath
    : currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);

  if (!pathMatches) return false;

  return relevantSearchMatches(currentSearch, targetSearch);
}

function WorkspaceTabs({
  items = [],
  listClassName = "",
  tabClassName = "",
}) {
  const location = useLocation();

  return (
    <div
      className={`wl-tabsList cd-tabs ol-tabs ${listClassName}`.trim()}
      role="tablist"
      aria-label="Workspace tabs"
    >
      {items.map((item, index) => {
        const active = isActiveLink(location, item);

        return (
          <Link
            key={`${item?.label || "tab"}-${index}`}
            to={item?.to || "#"}
            className={`wl-tabLink cd-tab ol-tab ${
              active ? "is-active" : ""
            } ${tabClassName}`.trim()}
            aria-current={active ? "page" : undefined}
          >
            {item?.label || "Tab"}
          </Link>
        );
      })}
    </div>
  );
}

WorkspaceTabs.displayName = "WorkspaceTabs";

export default memo(WorkspaceTabs);
